import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Radio, RadioGroup, Spinner } from "@heroui/react";
import { Icon } from "@iconify/react";
import { ModalControl, ButtonControl, LabelControl } from "doshx_controls_web";
import { setSelectedAddress } from "../../../reducers/slices/selection";
import { setContentModal, setMainModal } from "../../../reducers/slices/main";
import { address_list } from "../../../api/calls/user/address_list";
import { add_user_address } from "../../../api/calls/user/add_user_address";
import { delete_address } from "../../../api/calls/user/delete_address";
import { NewAddress } from "../../../components/address/components/new_address";
import { EmptyAddress } from "../../../components/address/components/empty-address";
export const UserAddresses = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [addressList, setAddressList] = useState([]);
    const [loadingAddresses, setLoadingAddresses] = useState(false);
    const [changeLoading, setChangeLoading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [addressToDelete, setAddressToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { user } = useSelector((state) => state.userState);
    const { address } = useSelector((state) => state.selectionState);
    const { _id: addressId } = address || {};
    useEffect(() => {
        if (user.user._id) {
            getAddressList();
        }
        else {
            navigate("/shop");
        }
    }, [user.user?._id]);
    const getAddressList = (props = { showLoader: true }) => {
        const { showLoader = true } = props;
        if (showLoader)
            setLoadingAddresses(true);
        address_list({
            user_id: user.user._id ?? "",
        }).then((response) => {
            setLoadingAddresses(false);
            if (response.success) {
                const data = response.content.addresses || [];
                setAddressList(data);
                // Find the address that's default property is true and set it as the selected address.
                const defaultAddress = data.find((address) => address.default);
                if (defaultAddress)
                    dispatch(setSelectedAddress(defaultAddress));
                dispatch(setContentModal(null));
            }
            else {
                dispatch(setMainModal({ title: "Something went wrong!", messages: [response.message] }));
            }
        });
    };
    const onChange = async (event) => {
        setChangeLoading(true);
        const id = event.target.value;
        if (!id)
            return;
        const address = { ...{ default: true }, ...addressList.find((address) => address._id === id) };
        if (address) {
            dispatch(setSelectedAddress(address));
            await add_user_address({
                default: true,
                latitude: address.latitude?.toString() ?? "",
                longitude: address.longitude?.toString() ?? "",
                full_address: address.full_address ?? "",
                line_one: address.line_one,
                type: address.type,
                city: address.city,
                label: address.label,
                apartment_number: address.apartment_number,
                postal_code: address.postal_code,
                is_residential: address.is_residential,
                business_name: address.business_name,
                complex_building_details: address.complex_building_details,
                unit_number: address.unit_number,
                suite_floor: address.suite_floor,
                notes: address.notes,
            });
            getAddressList({ showLoader: false });
            setChangeLoading(false);
        }
        else {
            setChangeLoading(false);
        }
    };
    const handleDeleteClick = (address) => {
        setAddressToDelete(address);
        setDeleteModalOpen(true);
    };
    const confirmDelete = () => {
        if (!addressToDelete?._id)
            return;
        setIsDeleting(true);
        delete_address({ _id: addressToDelete._id }).then((response) => {
            setIsDeleting(false);
            setDeleteModalOpen(false);
            setAddressToDelete(null);
            if (response.success) {
                getAddressList();
                dispatch(setMainModal({ title: "Success", messages: ["Address deleted successfully."] }));
            }
            else {
                dispatch(setMainModal({ title: "Something went wrong!", messages: [response.message] }));
            }
        });
    };
    const content = loadingAddresses ? (React.createElement("div", { className: "flex justify-center w-full py-24" },
        React.createElement(Spinner, { color: "primary", size: "lg", className: "w-full p-4" }))) : !loadingAddresses && addressList.length > 0 ? (React.createElement(React.Fragment, null,
        React.createElement(RadioGroup, { classNames: { wrapper: "gap-3" }, isRequired: true, value: addressId ?? "", name: "address-list", isDisabled: changeLoading, onChange: onChange }, addressList?.map((address) => (React.createElement("div", { key: address?._id, className: "flex flex-row justify-between items-center w-full gap-2 border border-neutral-gray rounded-xl px-4 py-3" },
            React.createElement("div", { className: "flex flex-row gap-4 justify-evenly" },
                React.createElement(Radio, { value: address?._id ?? "" }),
                React.createElement("div", { className: "flex flex-col" },
                    React.createElement("div", { className: "text-primary" }, address?.label),
                    React.createElement("div", { className: "text-dark-gray" }, address?.full_address))),
            React.createElement("div", { className: "flex gap-1" },
                React.createElement("button", { type: "button", title: "Delete address", className: "p-2 text-error hover:text-error/70 transition-colors", onClick: () => handleDeleteClick(address) },
                    React.createElement(Icon, { icon: "lucide:trash-2", width: "18", height: "18" })),
                React.createElement("button", { type: "button", title: "Edit address", className: "p-2 text-primary hover:text-primary/70 transition-colors", onClick: () => dispatch(setContentModal({
                        title: "Edit Address",
                        content: React.createElement(NewAddress, { addressToEdit: address, onComplete: () => getAddressList() }),
                        showCloseButton: true,
                        size: "4xl",
                    })) },
                    React.createElement(Icon, { icon: "lucide:square-pen", width: "18", height: "18" }))))))),
        React.createElement("div", { className: "pt-4" },
            React.createElement("button", { className: "text-primary text-semibold", onClick: () => dispatch(setContentModal({
                    title: "Add New Address",
                    content: React.createElement(NewAddress, { onComplete: () => getAddressList() }),
                    showCloseButton: true,
                    size: "4xl",
                })) },
                React.createElement("div", { className: "flex justify-between gap-2" },
                    React.createElement(Icon, { className: "my-auto", icon: "lucide:plus", width: "16", height: "16" }),
                    React.createElement("div", null, "Add Address")))))) : (React.createElement(EmptyAddress, { onAddClicked: () => {
            dispatch(setContentModal({
                title: "Add New Address",
                content: React.createElement(NewAddress, { onComplete: () => getAddressList() }),
                showCloseButton: true,
                size: "4xl",
            }));
        } }));
    return (React.createElement(React.Fragment, null,
        content,
        React.createElement(ModalControl, { title: "Delete Address", open: deleteModalOpen, onClose: () => setDeleteModalOpen(false), closeOnBackdrop: true, closeOnEscape: true },
            React.createElement("div", { className: "p-4" },
                React.createElement("p", { className: "text-dark-gray mb-4" }, "Are you sure you want to delete this address?"),
                addressToDelete && (React.createElement("div", { className: "p-3 bg-light-gray rounded-lg mb-4" },
                    React.createElement(LabelControl, { label: addressToDelete.label, bold: true, textColor: "text-borders-dark" }),
                    React.createElement(LabelControl, { label: addressToDelete.full_address, size: "small", textColor: "text-dark-gray" }))),
                React.createElement("p", { className: "text-sm text-dark-gray mb-6" }, "This action cannot be undone."),
                React.createElement("div", { className: "flex justify-end gap-3" },
                    React.createElement(ButtonControl, { label: "Cancel", backgroundColor: "bg-borders-dark", textColor: "text-white", onClick: () => setDeleteModalOpen(false), enabled: !isDeleting }),
                    React.createElement(ButtonControl, { label: isDeleting ? "Deleting..." : "Delete", backgroundColor: "bg-error", textColor: "text-white", onClick: confirmDelete, enabled: !isDeleting }))))));
};
